import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import supportService from '../../services/api/supportService';
import WebAuthnSetup from '../../components/admin/WebAuthnSetup';
import SupportMirrorButton from '../../components/support/SupportMirrorButton';

/* ---------- styled ---------- */
const Card = styled.div`
  background: #fff;
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  padding: 20px;
`;

const Title = styled.h1`
  margin: 0 0 4px;
  font-size: 1.4rem;
  color: var(--ve-ink-800);
`;

const Subtitle = styled.p`
  margin: 0 0 16px;
  color: #6b7280;
  font-size: 0.95rem;
`;

const Search = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  margin-bottom: 16px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.92rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 8px;
  border-bottom: 2px solid var(--border-subtle);
  color: #6b7280;
  font-weight: 600;
`;

const Td = styled.td`
  padding: 10px 8px;
  border-bottom: 1px solid #eef1f1;
  color: var(--ve-ink-800);
`;

const RoleTag = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 0.75rem;
  text-transform: capitalize;
  background: #eef2ff;
  color: #4338ca;
`;

const ViewButton = styled.button`
  border: 1px solid var(--act-primary);
  background: var(--act-primary);
  color: #fff;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  cursor: pointer;
  min-height: 40px;

  &:hover { background: #004080; }
  &:disabled { opacity: 0.6; cursor: default; }
`;

const Message = styled.p`
  color: #6b7280;
  padding: 16px 0;
`;

const ErrorText = styled.p`
  color: #b91c1c;
  padding: 8px 0;
`;

const SectionTitle = styled.h2`
  margin: 24px 0 8px;
  font-size: 1.1rem;
  color: var(--ve-ink-800);
`;

const dashboardFor = (userType) => {
  if (userType === 'customer') return '/customer/dashboard';
  if (userType === 'employee') return '/employee/calendar';
  return '/';
};

const SupportConsole = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();

  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async (term) => {
    setLoading(true);
    setError(null);
    try {
      const data = await supportService.listUsers({ search: term, limit: 100 });
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce the search so we do not query on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => load(search), 300);
    return () => clearTimeout(id);
  }, [search, load]);

  const handleImpersonate = async (user) => {
    setBusyId(user.id);
    setError(null);
    try {
      const target = await startImpersonation(user.id);
      navigate(dashboardFor(target.userType));
    } catch (err) {
      setError(err.message || 'Could not start impersonation');
      setBusyId(null);
    }
  };

  return (
    <>
    <Card>
      <Title>{t('support.console.title')}</Title>
      <Subtitle>{t('support.console.subtitle')}</Subtitle>

      <Search
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t('support.console.search')}
        aria-label={t('support.console.search')}
      />

      {error && <ErrorText>{error}</ErrorText>}

      {loading ? (
        <Message>{t('support.console.loading')}</Message>
      ) : users.length === 0 ? (
        <Message>{t('support.console.noUsers')}</Message>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('support.console.name')}</Th>
              <Th>{t('support.console.email')}</Th>
              <Th>{t('support.console.role')}</Th>
              <Th aria-label={t('support.console.action')} />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <Td>{`${u.firstName || ''} ${u.lastName || ''}`.trim() || '—'}</Td>
                <Td>{u.email}</Td>
                <Td><RoleTag>{t(`roles.${u.userType}`)}</RoleTag></Td>
                <Td style={{ textAlign: 'right' }}>
                  <ViewButton
                    type="button"
                    onClick={() => handleImpersonate(u)}
                    disabled={busyId === u.id}
                  >
                    {t('support.console.impersonate')}
                  </ViewButton>
                  <SupportMirrorButton userId={u.id} name={`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email} />
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>

    {/* Support users may register Windows Hello / a passkey for their own account. */}
    <SectionTitle>{t('support.console.security', { defaultValue: 'Security' })}</SectionTitle>
    <WebAuthnSetup />
    </>
  );
};

export default SupportConsole;
